import { decodeSessionPayload, encodeSessionPayload } from './auth/sessionEncoding';

export interface SessionUser {
  id: string;
  username: string;
  email?: string;
  name?: string;
  role: string;
}

interface AuthCookiePayload {
  authenticated: boolean;
  expiry: number;
  user?: object;
  timestamp?: number;
  authProvider?: string;
}

interface AuthSessionResponse {
  authenticated: boolean;
  user: SessionUser;
  expiry: number;
  authProvider: string;
}

export function parseAuthCookie(cookieValue: string): AuthCookiePayload {
  try {
    return decodeSessionPayload<AuthCookiePayload>(cookieValue);
  } catch {
    return JSON.parse(atob(cookieValue));
  }
}

export function buildAuthCookie(payload: AuthCookiePayload): string {
  return encodeSessionPayload(payload);
}

export const setCookie = (name: string, value: string, maxAge: number): void => {
  const secure = window.location.protocol === 'https:';
  const encodedValue = encodeURIComponent(value);
  document.cookie = `${name}=${encodedValue}; path=/; max-age=${maxAge}; SameSite=Lax${secure ? '; Secure' : ''}`;
};

export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length === 2) {
    const raw = parts.pop()?.split(';').shift() || null;
    return raw ? decodeURIComponent(raw) : null;
  }

  return null;
};

export const deleteCookie = (name: string): void => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

export function persistAppSession(user: SessionUser, sessionExpiry: number, authProvider: string): void {
  localStorage.setItem('isAuthenticated', 'true');
  localStorage.setItem('sessionExpiry', sessionExpiry.toString());
  localStorage.setItem('authUser', JSON.stringify(user));
  localStorage.setItem('authProvider', authProvider);
  localStorage.removeItem('sso_token');
  localStorage.removeItem('sso_state');
  localStorage.removeItem('sso_redirect');
  deleteCookie('authToken');
}

function hasValidLocalSession(): boolean {
  const localAuth = localStorage.getItem('isAuthenticated');
  const sessionExpiry = localStorage.getItem('sessionExpiry');

  if (localAuth !== 'true' || !sessionExpiry) {
    return false;
  }

  const expiryTime = parseInt(sessionExpiry, 10);
  return !Number.isNaN(expiryTime) && Date.now() <= expiryTime;
}

export const isAuthenticated = (): boolean => hasValidLocalSession();

export function getAuthProvider(): string | null {
  return localStorage.getItem('authProvider');
}

export const clearAppSession = (): void => {
  localStorage.removeItem('isAuthenticated');
  localStorage.removeItem('sessionExpiry');
  localStorage.removeItem('authUser');
  localStorage.removeItem('authProvider');
  localStorage.removeItem('sso_token');
  localStorage.removeItem('sso_state');
  localStorage.removeItem('sso_redirect');
  deleteCookie('authToken');
  deleteCookie('aiapps_session');
};

export async function syncSessionFromServer(): Promise<boolean> {
  try {
    const response = await fetch('/aiapps-auth/me', {
      credentials: 'include',
    });

    if (!response.ok) {
      clearAppSession();
      return false;
    }

    const payload = (await response.json()) as AuthSessionResponse;
    persistAppSession(payload.user, payload.expiry, payload.authProvider);
    return true;
  } catch {
    clearAppSession();
    return false;
  }
}

export const logout = async (): Promise<void> => {
  try {
    await fetch('/aiapps-auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
  } finally {
    clearAppSession();
    window.location.href = '/login';
  }
};

